import { Article } from '../models/models';
import { formatNumberWithSpaces } from './utils';

/**
 * Formate un décimal DB en chaîne éditable pour QuantiteInput (sans unités).
 * Utilisé en interne par QuantiteInput pour initialiser et normaliser le champ.
 *
 *   formatQuantiteEditable(1.1667, article)  →  "1-2"
 *   formatQuantiteEditable(3,      article)  →  "3"
 *   formatQuantiteEditable(5.5,    article)  →  "5.5"   (mode normal)
 */
export function formatQuantiteEditable(
  value: number,
  article: Article | null | undefined
): string {
  const ppc = article?.pieces_par_carton ?? null;
  const usePcsCts = !!(article?.allow_pcs_cts && ppc && ppc > 0);

  if (!usePcsCts) return value === 0 ? '' : String(value);

  const cartons = Math.floor(value);
  const pieces = Math.round((value - cartons) * ppc);

  if (pieces === 0) return String(cartons);
  return `${cartons}-${pieces}`;
}

/**
 * Formate un décimal DB en affichage lisible avec unités.
 *
 * Mode normal  (allow_pcs_cts = false) :
 *   formatQuantite(5, article)          →  "5 ctn"
 *   formatQuantite(5.5, article)        →  "5.5"          (pas d'unité si décimal brut)
 *
 * Mode cts-pcs (allow_pcs_cts = true, pieces_par_carton > 0) :
 *   formatQuantite(1.1667, article)     →  "1 ctn 2 pcs"  (ppc=12, unite="ctn", unite_sec="pcs")
 *   formatQuantite(3,      article)     →  "3 ctn"        (cartons entiers, sans pièces)
 *   formatQuantite(0.1667, article)     →  "0 ctn 2 pcs"  (moins d'un carton)
 *
 * Utilisé dans :
 *   - LigneDocumentVente.tsx  (colonne Quantité de MyTable)
 *   - QuantiteInput.tsx       (normalisation interne au blur)
 */
export function formatQuantite(
  value: number,
  article: Article | null | undefined
): string {
  const ppc = article?.pieces_par_carton ?? null;
  const usePcsCts = !!(article?.allow_pcs_cts && ppc && ppc > 0);
  const uniteCarton = article?.unite || null;
  const unitePiece = article?.unite_sec || null;

  if (!usePcsCts) {
    // Mode normal : affiche la valeur (formatée avec espaces) + l'unité principale si dispo
    if (!uniteCarton) return formatNumberWithSpaces(value);
    return Number.isInteger(value) ? `${formatNumberWithSpaces(value)} ${uniteCarton}` : formatNumberWithSpaces(value);
  }

  // Mode cts-pcs
  const cartons = Math.floor(value);
  const pieces = Math.round((value - cartons) * ppc);

  const ctsLabel = uniteCarton ?? 'cts';
  const pcsLabel = unitePiece ?? 'pcs';

  if (pieces === 0) return `${formatNumberWithSpaces(cartons)} ${ctsLabel}`;
  return `${formatNumberWithSpaces(cartons)} ${ctsLabel} ${formatNumberWithSpaces(pieces)} ${pcsLabel}`;
}
